import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { enumMarineInsuranceEntityType } from '@data-layer/enums/marine-insurance';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { IsfModel } from '@data-layer/models/isf';
import { LCLShipmentModel, QuoteModel } from '@data-layer/models/lcl';
import { MarineInsuranceManagementModel, MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { EntityModel } from '@data-layer/models/entities';
import { FormHelper } from '@helpers/form.helper';
import { MarineInsuranceBeneficiaryFromToFormItemEntitiesModel, MarineInsuranceBeneficiaryFromToFormItemModel } from '../../models';
import { marineInsuranceBeneficiaryItems } from '../../constants';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { takeUntil } from 'rxjs/operators';
import { enumEntityModelCode } from '@data-layer/enums/entities';

@Component({
  selector: 'app-marine-insurance-beneficiary-form',
  templateUrl: './marine-insurance-sidebar-beneficiary-form.component.html',
  styleUrls: ['./marine-insurance-sidebar-beneficiary-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsuranceSidebarBeneficiaryFormComponent implements OnInit, OnDestroy {
  @Input() public entity: enumMarineInsuranceEntityType;
  @Input() public marineInsurance: MarineInsuranceModel | MarineInsuranceManagementModel;
  @Input() public isf: IsfModel;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public lclQuote: QuoteModel;
  @Input() public lclShipment: LCLShipmentModel;
  @Input() public isSaving: boolean;
  @Input() public isManagement: boolean;

  @Output() public save: EventEmitter<EntityModel> = new EventEmitter<EntityModel>();

  public entityFormGroup: UntypedFormGroup;
  public formGroup: UntypedFormGroup;

  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;
  public errorsPhone: { [name: string]: string } = {
    maxlength: 'FORM_ERROR.MAX_LENGTH',
    validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER',
  };

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private fh: FormHelper, private cdr: ChangeDetectorRef) {}

  public detectChanges(): void {
    this.cdr.markForCheck();
  }

  public ngOnInit(): void {
    if (!this.isManagement) {
      this.entityFormGroup = this.fh.createEntityModelFormGroup(this.marineInsurance?.beneficiary?.entityCode, false);
    } else {
      this.entityFormGroup = this.fh.createEntityModelFormGroup(
        (this.marineInsurance as MarineInsuranceManagementModel)?.updatedBeneficiary?.entityCode ||
          this.marineInsurance?.beneficiary?.entityCode,
        false,
      );
    }
    this.formGroup = new UntypedFormGroup({
      beneficiaryItemType: new UntypedFormControl(null),
    });

    this.formGroup
      .get('beneficiaryItemType')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((entityCode: enumEntityModelCode) => {
        this.entityFormGroup.patchValue(this.entityData);
      });

    if (!!this.marineInsurance?.beneficiary) {
      this.entityFormGroup.patchValue(this.marineInsurance?.beneficiary);
      this.formGroup.patchValue({ beneficiaryItemType: this.marineInsurance?.beneficiary?.entityCode });
    }

    if (!!(this.marineInsurance as MarineInsuranceManagementModel)?.updatedBeneficiary) {
      this.entityFormGroup.patchValue((this.marineInsurance as MarineInsuranceManagementModel)?.updatedBeneficiary);
      this.formGroup.patchValue({
        beneficiaryItemType: (this.marineInsurance as MarineInsuranceManagementModel)?.updatedBeneficiary?.entityCode,
      });
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onSave(): void {
    this.entityFormGroup.markAllAsTouched();

    if (this.entityFormGroup.valid) {
      this.isSaving = true;
      this.save.emit({ ...this.entityFormGroup.value, entityCode: this.formGroup.get('beneficiaryItemType').value });
    }
  }

  public getBeneficiaryData(fieldToMatch: string): EntityModel {
    switch (this.entity) {
      case enumMarineInsuranceEntityType.ISF:
        return fieldToMatch === 'importer' ? this.isf?.importer : this.isf?.shipment?.[fieldToMatch];

      case enumMarineInsuranceEntityType.ENTRY_SUMMARY:
        return fieldToMatch === 'importer' ? this.entrySummary?.importer : this.entrySummary?.shipments?.[0]?.[fieldToMatch];

      case enumMarineInsuranceEntityType.LCL_QUOTE:
        return this.lclQuote?.[fieldToMatch];

      case enumMarineInsuranceEntityType.LCL_SHIPPING:
        return this.lclShipment?.[fieldToMatch];

      default:
        return null;
    }
  }

  public get beneficiaryItems(): MarineInsuranceBeneficiaryFromToFormItemEntitiesModel[] {
    return (
      (marineInsuranceBeneficiaryItems || []).find((item: MarineInsuranceBeneficiaryFromToFormItemModel) => item.entityType === this.entity)
        ?.entities || []
    );
  }

  public get entityData(): EntityModel {
    let data: EntityModel = this.getBeneficiaryData(
      this.beneficiaryItems.find(
        (item: MarineInsuranceBeneficiaryFromToFormItemEntitiesModel) =>
          item.entityCode === this.formGroup.get('beneficiaryItemType').value,
      )?.fieldToMatch,
    );

    if (this.formGroup.get('beneficiaryItemType').value === this.marineInsurance?.beneficiary?.entityCode) {
      data = { ...data, email: this.marineInsurance?.beneficiary?.email, phone: this.marineInsurance?.beneficiary?.phone };
    }

    return data;
  }
}
